import { SeededRng, type Size, type Vector2 } from '@sorrem/shared';
import { clamp, wrap } from './skill-tree-math';
import type { SkillTreeRgb } from './skill-tree-palette';

/**
 * The skill tree's painted Veil sky and its seeded motes, laid out in CSS
 * pixels for a page layer (docs/gameplay/skill-tree-ui.md, Backdrop). Both
 * follow the tree's pan and zoom at a fraction of its speed (parallax), so
 * the tree reads as floating in front of a distant sky.
 */
export interface VeilSkyView {
    /** Tree-space point at the center of the viewport (one node is 120 tree units). */
    readonly center: Vector2;
    /** CSS pixels per tree unit. */
    readonly zoom: number;
    /** The smallest zoom the tree allows; the sky and motes are at their smallest there and grow as the player zooms in. */
    readonly minZoom: number;
}

/** How much the painted sky moves with a pan, relative to the tree. */
const SKY_PAN_PARALLAX = 0.04;
/** How much the painted sky scales with zoom: scale = (zoom / minZoom) ^ this. About 1.5x at zoom 1 and 1.7x at 1.5. */
const SKY_ZOOM_PARALLAX = 0.3;
/** How much the mote field spreads with zoom: spacing = (zoom / minZoom) ^ this. About 2x at zoom 1 and 2.45x at 1.5. */
const MOTE_ZOOM_PARALLAX = 0.5;
/** How much each mote grows with zoom, relative to the field's spread. */
const MOTE_SIZE_ZOOM_SHARE = 0.5;
/** Extra coverage beyond the viewport, so parallax never exposes the sky's edges. */
const SKY_COVER_MARGIN = 1.15;
/** Aspect ratio of the painted sky image. */
export const VEIL_SKY_ASPECT = 16 / 9;

interface MoteLayerSpec {
    readonly count: number;
    /** Fraction of the tree's pan this layer moves (smaller is farther away). */
    readonly parallax: number;
    /** Color of the layer's motes. */
    readonly color: SkillTreeRgb;
    /** Mote diameter range in CSS pixels. */
    readonly minSize: number;
    readonly maxSize: number;
}

const MOTE_LAYERS: readonly MoteLayerSpec[] = [
    // Counts cover the tile at the smallest zoom; zooming in spreads the field, so fewer show at once.
    { count: 210, parallax: 0.12, color: [140, 115, 217], minSize: 1.5, maxSize: 3 },
    { count: 135, parallax: 0.25, color: [209, 189, 250], minSize: 2, maxSize: 4.5 },
    { count: 54, parallax: 0.45, color: [242, 217, 153], minSize: 3, maxSize: 6 },
];

interface Mote {
    /** Position within the layer's repeating tile, 0..1 on each axis. */
    readonly u: number;
    readonly v: number;
    /** Drift in tile fractions per second. */
    readonly driftU: number;
    readonly driftV: number;
    /** Diameter in CSS pixels. */
    readonly size: number;
    readonly twinklePhase: number;
    readonly twinkleSpeed: number;
}

export interface VeilSkyMoteLayer {
    readonly color: SkillTreeRgb;
    readonly parallax: number;
    readonly motes: readonly Mote[];
}

/** Where the painted sky sits: its top-left corner and size, in CSS pixels from the viewport's top-left. */
export interface VeilSkyImageLayout {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
}

/** How far the view is zoomed in past the smallest zoom (1 at the smallest). */
function zoomRatio(view: VeilSkyView): number {
    return Math.max(1, view.zoom / view.minZoom);
}

/** Sizes the sky to cover the viewport with margin at the smallest zoom, then grows and shifts it with the view. */
export function layoutVeilSkyImage(view: VeilSkyView, viewport: Size): VeilSkyImageLayout {
    const { width, height } = viewport;
    const zoomScale = Math.pow(zoomRatio(view), SKY_ZOOM_PARALLAX);
    const coverWidth = Math.max(width, height * VEIL_SKY_ASPECT) * SKY_COVER_MARGIN * zoomScale;
    const coverHeight = coverWidth / VEIL_SKY_ASPECT;
    const maxShiftX = (coverWidth - width) / 2;
    const maxShiftY = (coverHeight - height) / 2;
    const shiftX = clamp(-view.center.x * view.zoom * SKY_PAN_PARALLAX, -maxShiftX, maxShiftX);
    const shiftY = clamp(-view.center.y * view.zoom * SKY_PAN_PARALLAX, -maxShiftY, maxShiftY);
    return {
        x: (width - coverWidth) / 2 + shiftX,
        y: (height - coverHeight) / 2 + shiftY,
        width: coverWidth,
        height: coverHeight,
    };
}

/** Places every mote for a character, with `SeededRng` (the same seed, so a character's sky never changes). */
export function createVeilSkyMotes(seed: string): VeilSkyMoteLayer[] {
    const rng = new SeededRng(`veil-sky:${seed}`);
    return MOTE_LAYERS.map((spec) => ({
        color: spec.color,
        parallax: spec.parallax,
        motes: Array.from({ length: spec.count }, () => ({
            u: rng.random(),
            v: rng.random(),
            driftU: (rng.random() - 0.5) * 0.004,
            driftV: 0.001 + rng.random() * 0.003,
            size: spec.minSize + rng.random() * (spec.maxSize - spec.minSize),
            twinklePhase: rng.random() * Math.PI * 2,
            twinkleSpeed: 0.4 + rng.random() * 1.2,
        })),
    }));
}

/**
 * Visits every mote at `seconds`: tile position plus drift, offset by its
 * layer's pan parallax, wrapped to the tile, then spread about the viewport
 * center by the zoom. The tile covers the viewport at the smallest zoom, so
 * the field only ever spreads beyond it. Positions are CSS pixels from the
 * viewport's top-left; `size` is the diameter.
 */
export function forEachVeilSkyMote(
    layers: readonly VeilSkyMoteLayer[],
    view: VeilSkyView,
    viewport: Size,
    seconds: number,
    visit: (layerIndex: number, position: Vector2, size: number) => void,
): void {
    const tile = Math.max(viewport.width, viewport.height) * 1.2;
    const spread = Math.pow(zoomRatio(view), MOTE_ZOOM_PARALLAX);
    const sizeScale = Math.pow(spread, MOTE_SIZE_ZOOM_SHARE);
    layers.forEach((layer, layerIndex) => {
        // Divided by the spread so the on-screen pan parallax doesn't change with zoom.
        const offsetX = (view.center.x * view.zoom * layer.parallax) / spread;
        const offsetY = (view.center.y * view.zoom * layer.parallax) / spread;
        for (const mote of layer.motes) {
            const x = (wrap(mote.u * tile + mote.driftU * tile * seconds - offsetX, tile) - tile / 2) * spread;
            // Motes drift upward, as they did in the Babylon sky.
            const y = (wrap(mote.v * tile + mote.driftV * tile * seconds + offsetY, tile) - tile / 2) * spread;
            const twinkle = 0.55 + 0.45 * Math.sin(mote.twinklePhase + seconds * mote.twinkleSpeed);
            visit(layerIndex, { x: viewport.width / 2 + x, y: viewport.height / 2 - y }, mote.size * twinkle * sizeScale);
        }
    });
}
